import { REPORT_CATEGORY_OPTIONS } from "./constants";

export function activateOnKeyDown(event, callback) {
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  callback();
}

export function reorderItems(items, fromIndex, toIndex) {
  if (
    !Array.isArray(items) ||
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length
  ) {
    return items;
  }

  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);
  return nextItems;
}

export function scrollNodeIntoView(node) {
  if (!node) return;

  node.scrollIntoView({
    behavior: "smooth",
    block: "center",
    inline: "nearest",
  });

  if (typeof node.focus === "function") {
    node.focus({ preventScroll: true });
  }
}

export function buildOrderedPages(pages, orderedPageIds) {
  const pageMap = new Map((pages || []).map((page) => [page.id, page]));
  const nextPages = [];

  (orderedPageIds || []).forEach((pageId) => {
    const page = pageMap.get(pageId);
    if (page) {
      nextPages.push(page);
      pageMap.delete(pageId);
    }
  });

  pageMap.forEach((page) => {
    nextPages.push(page);
  });

  return nextPages;
}

function getProjectLineItems(form, t) {
  const periodValue =
    form?.period_start || form?.period_end
      ? `${form?.period_start || "-"} ~ ${form?.period_end || "-"}`
      : "-";

  return [
    {
      label: t("projectDetail.projectEditorBox.contractNameLabel", {
        defaultValue: "Contract",
      }),
      value: form?.contract_name || "-",
    },
    {
      label: t("projectDetail.projectEditorBox.contractorLabel", {
        defaultValue: "Contractor",
      }),
      value: form?.contractor || "-",
    },
    {
      label: t("projectDetail.projectEditorBox.periodLabel", {
        defaultValue: "Period",
      }),
      value: periodValue,
    },
    {
      label: t("projectDetail.projectEditorBox.purposeLabel", {
        defaultValue: "Purpose",
      }),
      value: form?.purpose || "-",
    },
  ];
}

function getReportMediaItems(file, t) {
  return [
    {
      id: "visual",
      label: t("imageFileIndex.report.ultrasonicDiagnosis", {
        defaultValue: "Ultrasonic",
      }),
      src: file?.image_visual || file?.markup_image_visual || "",
    },
    {
      id: "fft",
      label: "FFT",
      src: file?.markup_image_graph || "",
    },
    {
      id: "spectrogram",
      label: "SPECTROGRAM",
      src: file?.image_spectrogram || "",
    },
    {
      id: "thermal",
      label: t("imageFileIndex.report.thermalDiagnosis", {
        defaultValue: "Thermal",
      }),
      src:
        file?.markup_image_triplespot ||
        file?.markup_image_thermal ||
        file?.image_thermal ||
        "",
    },
    {
      id: "uv",
      label: t("imageFileIndex.report.ultravioletDiagnosis", {
        defaultValue: "Ultraviolet",
      }),
      src: file?.image_uv || "",
    },
  ];
}

function getPrimaryPreviewSource(mediaItems = []) {
  return mediaItems.find((item) => item?.src)?.src || "";
}

export function getSafeImageReportDraftData(file = {}) {
  return {
    ...file,
    click_points: Array.isArray(file?.click_points) ? file.click_points : [],
    click_areas: Array.isArray(file?.click_areas) ? file.click_areas : [],
  };
}

function hasCompleteElectricalClickPoints(candidate = {}) {
  return (
    Array.isArray(candidate?.click_points) &&
    candidate.click_points.length >= 3 &&
    candidate.click_points.slice(0, 3).every((point) => Boolean(point?.value))
  );
}

export function requiresElectricalTripleSpotAnalysis(file = {}) {
  return !file?.markup_image_triplespot || !hasCompleteElectricalClickPoints(file);
}

export function mergeUd1000PreviewImages(file = {}, images = {}) {
  if (file?.file_type === "video") {
    return {
      ...images,
      graph_image_1: images?.graph_image_1 || file?.graph_image_1 || "",
      graph_image_2: images?.graph_image_2 || file?.graph_image_2 || "",
      frame_image_1: images?.frame_image_1 || file?.frame_image_1 || "",
      frame_image_2: images?.frame_image_2 || file?.frame_image_2 || "",
      waveform_image_1: images?.waveform_image_1 || file?.waveform_image_1 || "",
      waveform_image_2: images?.waveform_image_2 || file?.waveform_image_2 || "",
    };
  }

  return {
    ...images,
    image_visual:
      images?.image_visual ||
      file?.image_visual ||
      file?.markup_image_visual ||
      "",
    markup_image_graph:
      images?.markup_image_graph || file?.markup_image_graph || "",
    image_spectrogram:
      images?.image_spectrogram || file?.image_spectrogram || "",
    markup_image_thermal:
      images?.markup_image_thermal ||
      file?.markup_image_thermal ||
      file?.image_thermal ||
      "",
    markup_image_triplespot:
      images?.markup_image_triplespot || file?.markup_image_triplespot || "",
    image_uv: images?.image_uv || file?.image_uv || "",
  };
}

export function buildElectricalPreviewImages(file = {}) {
  const isReady = !requiresElectricalTripleSpotAnalysis(file);

  return {
    image_visual: file?.image_visual || "",
    markup_image_triplespot: isReady ? file?.markup_image_triplespot || "" : "",
  };
}

export function buildUd1000PreviewMediaItems(file = {}, images = {}, t) {
  if (file?.file_type === "video") {
    return [
      {
        id: "frame-a",
        label: t("videoFileIndex.report.ultrasonicDiagnosisWithTime", {
          label: "A",
          time: file?.time_1 ?? "",
          defaultValue: "Ultrasonic A",
        }),
        src: images?.frame_image_1 || "",
      },
      {
        id: "fft-a",
        label: t("videoFileIndex.report.fastFourierTransformWithLabel", {
          label: "A",
          defaultValue: "FFT A",
        }),
        src: images?.graph_image_1 || "",
      },
      {
        id: "wave-a",
        label: t("videoFileIndex.report.waveformWithLabel", {
          label: "A",
          defaultValue: "Waveform A",
        }),
        src: images?.waveform_image_1 || "",
      },
      {
        id: "frame-b",
        label: t("videoFileIndex.report.ultrasonicDiagnosisWithTime", {
          label: "B",
          time: file?.time_2 ?? "",
          defaultValue: "Ultrasonic B",
        }),
        src: images?.frame_image_2 || "",
      },
      {
        id: "fft-b",
        label: t("videoFileIndex.report.fastFourierTransformWithLabel", {
          label: "B",
          defaultValue: "FFT B",
        }),
        src: images?.graph_image_2 || "",
      },
      {
        id: "wave-b",
        label: t("videoFileIndex.report.waveformWithLabel", {
          label: "B",
          defaultValue: "Waveform B",
        }),
        src: images?.waveform_image_2 || "",
      },
    ];
  }

  return [
    {
      id: "visual",
      label: t("imageFileIndex.report.ultrasonicDiagnosis", {
        defaultValue: "Ultrasonic",
      }),
      src: images?.image_visual || "",
    },
    {
      id: "fft",
      label: t("imageFileIndex.report.fastFourierTransform", {
        defaultValue: "FFT",
      }),
      src: images?.markup_image_graph || "",
    },
    {
      id: "spectrogram",
      label: "SPECTROGRAM",
      src: images?.image_spectrogram || "",
    },
    {
      id: "thermal",
      label: t("imageFileIndex.report.thermalDiagnosis", {
        defaultValue: "Thermal",
      }),
      src:
        images?.markup_image_triplespot ||
        images?.markup_image_thermal ||
        "",
    },
    {
      id: "uv",
      label: t("imageFileIndex.report.ultravioletDiagnosis", {
        defaultValue: "Ultraviolet",
      }),
      src: images?.image_uv || "",
    },
  ];
}

export function getProjectReportPages(
  project,
  form,
  files,
  t,
  selectedReportCategory = "",
) {
  const safeFiles = Array.isArray(files) ? files : [];
  const imageCount = safeFiles.filter((file) => file?.file_type === "image").length;
  const videoCount = safeFiles.filter((file) => file?.file_type === "video").length;
  const otherCount = Math.max(safeFiles.length - imageCount - videoCount, 0);
  const totalPages = safeFiles.length;
  const electricalReadyCount =
    selectedReportCategory === "electrical_duty"
      ? safeFiles.filter((file) => !requiresElectricalTripleSpotAnalysis(file)).length
      : 0;
  const electricalPendingCount =
    selectedReportCategory === "electrical_duty"
      ? Math.max(totalPages - electricalReadyCount, 0)
      : 0;
  const electricalCoverageRatio =
    selectedReportCategory === "electrical_duty" && totalPages > 0
      ? electricalReadyCount / totalPages
      : 0;
  const electricalCoveragePercent = Math.round(electricalCoverageRatio * 100);
  const projectLineItems = getProjectLineItems(form, t);
  const periodValue =
    form?.period_start || form?.period_end
      ? `${form?.period_start || "-"} ~ ${form?.period_end || "-"}`
      : "-";
  const projectName =
    form?.project_name ||
    project?.project_name ||
    t("projectDetail.projectReportEditor.defaultProjectTitle", {
      defaultValue: "Project Report",
    });

  const projectSummary = {
    title: projectName,
    lineItems: projectLineItems,
    cover: {
      title:
        form?.contract_name ||
        project?.contract_name ||
        projectName,
      contractor: form?.contractor || "-",
      period: periodValue,
    },
    lines: projectLineItems.map(
      (item) => `${item.label}: ${item.value}`,
    ),
    stats: [
      {
        label: t("projectDetail.projectReportEditor.totalPagesLabel", {
          defaultValue: "Pages",
        }),
        value: String(totalPages),
      },
      {
        label: t("projectDetail.projectReportEditor.imageFiles", {
          defaultValue: "Images",
        }),
        value: String(imageCount),
      },
      {
        label: t("projectDetail.projectReportEditor.videoFiles", {
          defaultValue: "Videos",
        }),
        value: String(videoCount),
      },
    ],
    chart: {
      mode: selectedReportCategory === "electrical_duty" ? "electrical_duty" : "default",
      totalFiles: safeFiles.length,
      totalPages,
      imageCount,
      videoCount,
      otherCount,
      readyCount: electricalReadyCount,
      pendingCount: electricalPendingCount,
      coverageRatio: electricalCoverageRatio,
      centerValue:
        selectedReportCategory === "electrical_duty"
          ? `${electricalCoveragePercent}%`
          : String(totalPages),
      centerLabel:
        selectedReportCategory === "electrical_duty"
          ? t("projectDetail.projectReportEditor.electricalCoverageLabel", {
              defaultValue: "3-Phase Coverage",
            })
          : t("projectDetail.projectReportEditor.totalPagesLabel", {
              defaultValue: "Pages",
            }),
    },
    legend:
      selectedReportCategory === "electrical_duty"
        ? [
            {
              label: t("projectDetail.projectReportEditor.electricalCoverageReady", {
                defaultValue: "3-phase ready",
              }),
              value: String(electricalReadyCount),
              toneClassName: "is-ready",
            },
            {
              label: t("projectDetail.projectReportEditor.electricalCoveragePending", {
                defaultValue: "Analysis needed",
              }),
              value: String(electricalPendingCount),
              toneClassName: "is-pending",
            },
          ]
        : [
            {
              label: t("projectDetail.projectReportEditor.imageFiles", {
                defaultValue: "Images",
              }),
              value: String(imageCount),
              toneClassName: "is-image",
            },
            {
              label: t("projectDetail.projectReportEditor.videoFiles", {
                defaultValue: "Videos",
              }),
              value: String(videoCount),
              toneClassName: "is-video",
            },
            {
              label: t("projectDetail.projectReportEditor.otherFiles", {
                defaultValue: "Others",
              }),
              value: String(otherCount),
              toneClassName: "is-other",
            },
          ],
  };

  const pages = safeFiles.map((file, index) => {
    const requiresElectricalAnalysis =
      selectedReportCategory === "electrical_duty"
        ? requiresElectricalTripleSpotAnalysis(file)
        : false;
    const mediaItems = getReportMediaItems(file, t);
    const previewImage = getPrimaryPreviewSource(mediaItems);

    return {
      id: file?.upload_id || `project-file-${index + 1}`,
      title:
        file?.clean_file_name ||
        file?.file_name ||
        t("projectDetail.projectReportEditor.fileFallbackTitle", {
          defaultValue: "Untitled file",
        }),
      subtitle:
        file?.facility_name ||
        file?.facility_type ||
        t("projectDetail.fileList.table.facilityName", {
          defaultValue: "Facility",
        }),
      typeLabel: file?.file_type || "-",
      pageNumber: index + 1,
      meta: [
        {
          label: t("projectDetail.fileList.table.facilityType", {
            defaultValue: "Facility type",
          }),
          value: file?.facility_type || "-",
        },
        {
          label: t("projectDetail.fileList.table.facilityName", {
            defaultValue: "Facility name",
          }),
          value: file?.facility_name || "-",
        },
        {
          label: t("projectDetail.fileList.table.location", {
            defaultValue: "Location",
          }),
          value: file?.location || "-",
        },
        {
          label: t("imageFileIndex.report.shootingDate", {
            defaultValue: "Shooting date",
          }),
          value: file?.shooting_date || "-",
        },
      ],
      note:
        file?.inspection_result ||
        file?.fusion_result ||
        t("projectDetail.projectReportEditor.noDiagnosisNote", {
          defaultValue: "No diagnosis note.",
        }),
      requiresElectricalAnalysis,
      previewImage,
      mediaItems,
      file,
    };
  });

  if (pages.length) {
    return {
      projectSummary,
      pages,
    };
  }

  return {
    projectSummary,
    pages: [
      {
        id: "project-report-empty",
        title: t("projectDetail.projectReportEditor.emptyTitle", {
          defaultValue: "No files available",
        }),
        subtitle: projectName,
        typeLabel: "-",
        pageNumber: 1,
        meta: [],
        note: t("projectDetail.projectReportEditor.emptyDescription", {
          defaultValue: "There are no files to preview in this report yet.",
        }),
        previewImage: "",
        mediaItems: [],
        isEmpty: true,
      },
    ],
  };
}

export function buildProjectReportChartStyle(chart = {}) {
  if (chart.mode === "electrical_duty") {
    const readyAngle = (Math.max(Number(chart.coverageRatio) || 0, 0) % 1) * 360;

    return {
      background: `conic-gradient(
        #0f766e 0deg ${readyAngle}deg,
        #fda4af ${readyAngle}deg 360deg
      )`,
    };
  }

  const total = Math.max(Number(chart.totalFiles) || 0, 1);
  const imageAngle = ((Number(chart.imageCount) || 0) / total) * 360;
  const videoAngle = ((Number(chart.videoCount) || 0) / total) * 360;
  const otherAngle = Math.max(360 - imageAngle - videoAngle, 0);

  return {
    background: `conic-gradient(
      #2563eb 0deg ${imageAngle}deg,
      #0f766e ${imageAngle}deg ${imageAngle + videoAngle}deg,
      #cbd5e1 ${imageAngle + videoAngle}deg ${imageAngle + videoAngle + otherAngle}deg
    )`,
  };
}

export function getSelectedReportCategoryLabel(selectedReportCategory, t) {
  const option = REPORT_CATEGORY_OPTIONS.find(
    (item) => item.id === selectedReportCategory,
  );

  if (!option) return "";

  return t(option.labelKey, {
    defaultValue: option.defaultLabel,
  });
}
